import { AbilityProvider as CaslAbilityProvider } from '@casl/react';
import { useEffect, useState, type ReactNode } from 'react';

import { useSessionStore } from '@/core/auth/session-store';

import { createAppAbility, rulesFromSession, type AppAbility } from './ability';

/**
 * One ability instance for the app's lifetime, mutated via `update()`, not recreated: constructing
 * a new instance on session change would discard the subscription `<Can>` consumers rely on — the
 * standard CASL staleness bug, stale permissions after a mid-session change. Seeded deny-all.
 */
export const ability: AppAbility = createAppAbility(rulesFromSession(null));

/**
 * One-time seed from the settled session, called in bootstrap BEFORE the router is constructed. The
 * initial navigation's ability loaders (`requireAbility`) run before this provider mounts, so
 * without the seed a hard load reads the deny-all default and 403s an authorized user. Ongoing sync
 * — including for later navigations' loaders — is the provider's subscription below.
 */
export function seedAbility(): void {
  ability.update(rulesFromSession(useSessionStore.getState().user));
}

export function AbilityProvider({ children }: { children: ReactNode }) {
  const user = useSessionStore((state) => state.user);
  const [lastUser, setLastUser] = useState<typeof user>(null);

  // Synchronizes before paint, not in useEffect: an effect runs after paint, so a child would
  // render once against the previous user's rules — the flash of stale permissions this guards
  // against. Compares against useState rather than a ref, since mutating a ref during render is
  // unsafe under Strict Mode's double-render (a discarded render still applies it).
  if (lastUser !== user) {
    setLastUser(user);
    ability.update(rulesFromSession(user));
  }

  useEffect(() => {
    // Also reacts to changes that do not alter the user object identity, and keeps the ability
    // current for loaders on later navigations.
    const unsubscribe = useSessionStore.subscribe((state) => {
      ability.update(rulesFromSession(state.user));
    });
    return unsubscribe;
  }, []);

  return <CaslAbilityProvider value={ability}>{children}</CaslAbilityProvider>;
}
